import { ProductCodeInputField, PlatformInputField, QuestionBuilderFormMemo } from "./components/form";
import { css } from "@emotion/react";
import { Card, FloatButton, Form, Modal, Skeleton, Typography } from "antd";
import { ApartmentOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { IntelligenceFormFieldsEnum } from "./enum";
import React from "react";
import { QueryDependenciesGraphProvider } from "./context/questions-context";
import { DependenciesGraph } from "./components/question-graph/dependencies-graph";
import { ControlQuestionFloat } from "./components/control-question-float";
import { SyncFormToUrl } from "./components/sync-form-to-url";
import { FormInstance } from "antd/lib";
import { AddNewQuestionButton } from "./components/form/add-new-question-button";
import { useMeta } from "./hooks/use-meta";
import { SmlAsset } from "@src/types/intelligence-search";
import { UseHintContent } from "../intelligence-search/list";
import { PERMISSION_FORCED_DISABLED_CLASS, PERMISSION_LAYOUT_CHECK } from "@src/components/crud-permission-layout";

export const generateQuestionElementId = (questionId: string) => {
    return "multiple-question-intelligence-search" + questionId;
};

export const IntelligenceSearchV2: React.FC<{
    initialValues?: any;
    onChange?: (v: any) => void;
    form: FormInstance<any>;
}> = ({ initialValues, onChange, form }) => {
    const productCode = Form.useWatch(IntelligenceFormFieldsEnum.PRODUCT_CODE, form);
    const platform = Form.useWatch(IntelligenceFormFieldsEnum.PLATFORM, form);

    const { meta, refetch, isLoading: isMetaDictionaryLoading } = useMeta({ productCode });

    React.useEffect(() => {
        if (productCode) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productCode]);
    const metaDictionary: Record<string, SmlAsset | undefined> = React.useMemo(() => {
        const metaApi = (meta ?? []).reduce((acc, currItem) => {
            acc[currItem.name] = currItem;
            return acc;
        }, {} as Record<string, SmlAsset>);
        if (!metaApi["ama_app_id"]) {
            metaApi["ama_app_id"] = {
                id: "dimension.ama_app_id",
                name: "ama_app_id",
                asset_type: "dimension",
                title: "",
                data_type: "string",
                metadata: {},
            };
        }
        if (!metaApi["date_tzutc"]) {
            metaApi["date_tzutc"] = {
                id: "dimension.date_tzutc",
                name: "date_tzutc",
                asset_type: "dimension",
                title: "",
                data_type: "date",
                metadata: {},
            };
        }

        return metaApi;
    }, [meta]);
    return (
        <Form form={form} layout="vertical" name="is-multi-questions" initialValues={initialValues}>
            <Card
                className="mb-4"
                css={css`
                    .ant-card-body {
                        background-color: white !important;
                        border-color: #1890ff !important;
                        border-style: solid !important;
                        border-width: 1px !important;
                        border-radius: 8px !important;
                    }
                `}
            >
                <div className={`w-full  ${PERMISSION_LAYOUT_CHECK}`}>
                    <div
                        className="flex justify-between"
                        css={css`
                            .ant-form-item {
                                margin-bottom: 0 !important;
                            }
                        `}
                    >
                        <div className="flex justify-start space-x-4">
                            <Form.Item
                                name={IntelligenceFormFieldsEnum.PRODUCT_CODE}
                                required
                                className={`${PERMISSION_FORCED_DISABLED_CLASS}`}
                                rules={[{ required: true, message: "Please select product" }]}
                            >
                                <ProductCodeInputField form={form} />
                            </Form.Item>
                            <Form.Item
                                name={IntelligenceFormFieldsEnum.PLATFORM}
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select platform",
                                    },
                                ]}
                            >
                                <PlatformInputField form={form} />
                            </Form.Item>
                        </div>
                        <UseHintContent isEnoughRequireField={!!productCode && !!platform} placement="left" />
                    </div>
                </div>
            </Card>
            {isMetaDictionaryLoading && <Skeleton active />}
            {!isMetaDictionaryLoading && (
                <QueryDependenciesGraphProvider productCode={productCode} metaDictionary={metaDictionary}>
                    <div className="space-y-2">
                        <Form.List name={IntelligenceFormFieldsEnum.QUESTIONS}>
                            {(fields, { add, remove }) => {
                                return (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <div
                                                css={css`
                                                    .ant-form-item {
                                                        margin-bottom: 24px !important;
                                                    }
                                                `}
                                            >
                                                <Form.Item name={[name]}>
                                                    <QuestionBuilderFormMemo
                                                        key={key}
                                                        formListFieldData={{
                                                            key,
                                                            name,
                                                            ...restField,
                                                        }}
                                                        productCode={productCode}
                                                        platform={platform}
                                                        add={add}
                                                        remove={remove}
                                                    />
                                                </Form.Item>
                                            </div>
                                        ))}
                                        <AddNewQuestionButton
                                            add={add}
                                            productCode={productCode}
                                            platform={platform}
                                            fields={fields}
                                        />
                                    </>
                                );
                            }}
                        </Form.List>
                        <QuestionToolbox form={form} productCode={productCode} platform={platform} />
                    </div>
                </QueryDependenciesGraphProvider>
            )}
            <ControlQuestionFloat form={form} />
            <SyncFormToUrl form={form} onChange={onChange} />
        </Form>
    );
};

const QuestionToolbox: React.FC<{ form: FormInstance; productCode?: string; platform?: string[] }> = ({
    form,
    productCode,
    platform,
}) => {
    const [shouldShowDependenciesGraph, showDependenciesGraph] = React.useState(false);
    const [shouldShowFormState, showFormState] = React.useState(false);
    const questions = Form.useWatch(IntelligenceFormFieldsEnum.QUESTIONS, form);

    return (
        <>
            <FloatButton.Group shape="circle" style={{ insetInlineEnd: 24, zIndex: 10, right: 10 }}>
                <FloatButton
                    onClick={() => showDependenciesGraph(true)}
                    badge={{ count: (questions || []).length, color: "blue" }}
                    icon={<ApartmentOutlined />}
                />
                <FloatButton onClick={() => showFormState(true)} icon={<QuestionCircleOutlined />} />
            </FloatButton.Group>

            <Modal
                className="w-[1200px]"
                open={shouldShowFormState}
                title="Form state"
                centered
                onCancel={() => showFormState(false)}
                footer={(_, { CancelBtn }) => (
                    <>
                        <CancelBtn />
                    </>
                )}
            >
                <div className="max-h-[80vh] overflow-y-scroll">
                    <Typography>
                        <pre>{JSON.stringify(form.getFieldsValue(), null, 2)}</pre>
                    </Typography>
                </div>
            </Modal>

            <Modal
                width={"90%"}
                open={shouldShowDependenciesGraph}
                title="Dependencies graph"
                centered
                onCancel={() => showDependenciesGraph(false)}
                footer={(_, { CancelBtn }) => (
                    <>
                        <CancelBtn />
                    </>
                )}
            >
                <div className="max-h-[80vh] overflow-y-scroll">
                    <DependenciesGraph
                        hidden={productCode === undefined || platform === undefined || questions === undefined}
                    />
                </div>
            </Modal>
        </>
    );
};
